/**
 * Conversion between the two spellings of "which page is this", which are
 * deliberately different types and must not be confused:
 *
 * - **slug** — the identifier fragment stored on a content type: `kontakt/p`.
 *   No leading slash, no trailing slash. Enforced on write by `SLUG_FORMAT`
 *   (`packages/cms/src/features/redirects/slugValidation.ts`) and by the
 *   `regex` on every slug attribute in the Strapi content-type schemas under
 *   `packages/cms/src/api`.
 * - **path** — the URL: `/kontakt/p/`. All-lowercase, no repeated slashes,
 *   **with** trailing slash, per spec 10.99-Technical-Requirements § canonical
 *   form and `trailingSlash: true` in `packages/website/next.config.ts`.
 *
 * Storing one and comparing it to the other is what made every literal Page
 * 404: the catch-all built `/kontakt/p/` and Page Selection matched it against
 * the column verbatim. These two functions are the only sanctioned crossing
 * points, so a producer can no longer invent a third spelling.
 *
 * Lives in `packages/shared` because the website (route → `fallbackSlug`) and
 * the backend (slug → CDN purge URL, sitemap `<loc>`, canonical) must agree
 * byte-for-byte. `packages/cms` does **not** depend on this package and keeps
 * its own equivalents in `features/redirects/paths.ts` and
 * `SLUG_INTERCEPTIONS.buildPath`; that duplication is a package-boundary cost,
 * not an oversight — do not "unify" it by giving Strapi a dependency on shared.
 */

/**
 * The locale that renders without a path prefix. `next-intl` runs with
 * `localePrefix: 'as-needed'` and `defaultLocale: 'de'` on every brand
 * (`packages/website/i18n/routing.ts`), so only non-`de` paths carry a prefix.
 *
 * Duplicated as `DEFAULT_LOCALE` in `packages/cms/src/features/redirects/constants.ts`
 * for the same package-boundary reason described above.
 */
export const DEFAULT_LOCALE = 'de';

/** Collapse repeated slashes and drop the leading/trailing ones. */
function trimSlashes(value: string): string {
  return value.replace(/\/{2,}/g, '/').replace(/^\/+|\/+$/g, '');
}

/**
 * Bare slug → canonical page path, locale-prefixed for non-default locales.
 *
 * Tolerant of a slug that already carries slashes: a row written before the
 * format was enforced, or by an import that bypasses Strapi lifecycles, must
 * not produce `//preise//`. An empty slug is the site root.
 */
export function pagePathFromSlug(slug: string, locale?: string | null): string {
  const trimmed = trimSlashes(slug);
  const prefix = !locale || locale === DEFAULT_LOCALE ? '' : `/${locale}`;
  return trimmed ? `${prefix}/${trimmed}/` : `${prefix}/`;
}

/**
 * Page path → the bare slug to match against the stored column.
 *
 * Does **not** strip a locale prefix: only the caller knows the configured
 * locale set, and a slug may legitimately begin with a segment that looks like
 * a locale code. Callers holding a locale-prefixed path (e.g. the `(main)`
 * layout, which reads `x-middleware-request-x-pathname`) must strip it first.
 *
 * Does **not** lowercase either — `normalizePathname` in the website proxy has
 * already done that and issued the 301 by the time a route sees the segments.
 */
export function slugFromPagePath(path: string): string {
  return trimSlashes(path);
}
